import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getAuthConfig } from '../utils/auth';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Upload, FileText, Calculator, Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function AITakeoff() {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [results, setResults] = useState(null);
  const [preview, setPreview] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setResults(null);
      
      // Create preview for images
      if (selectedFile.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreview(reader.result);
        };
        reader.readAsDataURL(selectedFile);
      } else {
        setPreview(null);
      }
    }
  };

  const handleAnalyze = async () => {
    if (!file) {
      toast.error('Please upload a building plan first');
      return;
    }

    setAnalyzing(true);
    try {
      // Check authentication
      await axios.get(`${BACKEND_URL}/api/auth/me`, getAuthConfig());

      // Create form data
      const formData = new FormData();
      formData.append('file', file);

      // Upload and analyze
      const response = await axios.post(
        `${BACKEND_URL}/api/takeoff/analyze`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      setResults(response.data);
      toast.success('Building plan analyzed successfully!');
    } catch (error) {
      if (error.response?.status === 401) {
        const redirectUrl = window.location.origin + '/ai-takeoff';
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
      } else {
        toast.error(error.response?.data?.detail || 'Failed to analyze building plan');
      }
    } finally {
      setAnalyzing(false);
    }
  };

  const exportToCSV = () => {
    if (!results) return;

    const csv = [
      ['Material', 'Quantity', 'Unit', 'Notes'].join(','),
      ...results.materials.map(m => 
        [m.name, m.quantity, m.unit, m.notes || ''].join(',')
      )
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'material-takeoff.csv';
    a.click();
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <Navbar />
      
      <div className="pt-32 pb-20 px-4 sm:px-6 lg:px-8" data-testid="ai-takeoff-page">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12 animate-fade-in">
            <div className="inline-block mb-4">
              <span className="px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-semibold">
                AI-Powered Analysis
              </span>
            </div>
            <h1 className="font-bold text-4xl md:text-6xl tracking-tight leading-tight mb-6" data-testid="takeoff-title">
              AI Material Takeoff
            </h1>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Upload your building plans and let our AI calculate comprehensive material quantities including drywall, roofing, flooring, siding, doors, concrete, rebar, and paint.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Upload Section */}
            <div className="bg-white rounded-3xl shadow-xl p-8 border border-gray-100">
              <div className="flex items-center mb-6">
                <Upload className="w-6 h-6 text-primary mr-3" />
                <h2 className="text-2xl font-bold">Upload Building Plans</h2>
              </div>

              {/* File Input */}
              <div className="mb-6">
                <label className="block w-full">
                  <div className="border-2 border-dashed border-gray-300 rounded-2xl p-12 text-center cursor-pointer hover:border-primary transition-colors">
                    <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 mb-2">Click to upload or drag and drop</p>
                    <p className="text-sm text-gray-500">PDF, PNG, JPG (Max 10MB)</p>
                    <input
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg"
                      onChange={handleFileChange}
                      className="hidden"
                      data-testid="file-upload-input"
                    />
                  </div>
                </label>

                {file && (
                  <div className="mt-4 p-4 bg-blue-50 rounded-2xl border border-blue-100">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <FileText className="w-5 h-5 text-primary mr-2" />
                        <span className="font-medium">{file.name}</span>
                      </div>
                      <button
                        onClick={() => {
                          setFile(null);
                          setPreview(null);
                          setResults(null);
                        }}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Preview */}
              {preview && (
                <div className="mb-6">
                  <h3 className="font-semibold mb-3">Preview</h3>
                  <img src={preview} alt="Plan preview" className="w-full rounded-2xl border border-gray-200" />
                </div>
              )}

              {/* Analyze Button */}
              <Button
                onClick={handleAnalyze}
                disabled={!file || analyzing}
                className="w-full h-14 text-lg font-bold rounded-2xl bg-gradient-to-r from-primary to-accent hover:shadow-xl transition-all"
                data-testid="analyze-button"
              >
                {analyzing ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Analyzing Plans...
                  </>
                ) : (
                  <>
                    <Calculator className="w-5 h-5 mr-2" />
                    Analyze Building Plans
                  </>
                )}
              </Button>

              {/* Info */}
              <div className="mt-6 p-4 bg-gray-50 rounded-2xl">
                <p className="text-sm text-gray-600">
                  <strong>What we analyze:</strong>
                  <br />• Drywall (walls & ceilings in sqft & squares)
                  <br />• Roofing (squares with waste factor)
                  <br />• Flooring (sqft by type with waste factor)
                  <br />• Siding (exterior coverage in sqft)
                  <br />• Doors (interior & exterior quantities)
                  <br />• Concrete & Rebar (cubic yards & linear feet)
                  <br />• Paint (primer & finish in gallons)
                </p>
              </div>
            </div>

            {/* Results Section */}
            <div className="bg-white rounded-3xl shadow-xl p-8 border border-gray-100">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                  <Calculator className="w-6 h-6 text-primary mr-3" />
                  <h2 className="text-2xl font-bold">Material Breakdown</h2>
                </div>
                {results && (
                  <Button
                    onClick={exportToCSV}
                    variant="outline"
                    size="sm"
                    className="rounded-full"
                    data-testid="export-csv-button"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export CSV
                  </Button>
                )}
              </div>

              {!results && !analyzing && (
                <div className="text-center py-12">
                  <Calculator className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">Upload and analyze plans to see material breakdown</p>
                </div>
              )}

              {analyzing && (
                <div className="text-center py-12">
                  <Loader2 className="w-16 h-16 text-primary mx-auto mb-4 animate-spin" />
                  <p className="text-gray-600 font-medium">AI is analyzing your building plans...</p>
                  <p className="text-sm text-gray-500 mt-2">This may take 10-30 seconds</p>
                </div>
              )}

              {results && (
                <div className="space-y-6" data-testid="takeoff-results">
                  {/* Summary */}
                  <div className="p-6 bg-gradient-to-br from-primary/5 to-accent/5 rounded-2xl border border-primary/10">
                    <h3 className="font-bold text-lg mb-3">Summary</h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <div className="text-gray-600">Total Area</div>
                        <div className="font-bold text-xl text-primary">{results.total_area} sqft</div>
                      </div>
                      <div>
                        <div className="text-gray-600">Materials Found</div>
                        <div className="font-bold text-xl text-primary">{results.materials.length}</div>
                      </div>
                    </div>
                  </div>

                  {/* Materials List */}
                  <div>
                    <h3 className="font-bold text-lg mb-4">Materials Required</h3>
                    <div className="space-y-3">
                      {results.materials.map((material, idx) => (
                        <div key={idx} className="p-4 border border-gray-200 rounded-2xl hover:border-primary/30 transition-colors">
                          <div className="flex justify-between items-start mb-2">
                            <h4 className="font-semibold text-lg">{material.name}</h4>
                            <span className="font-bold text-primary">
                              {material.quantity} {material.unit}
                            </span>
                          </div>
                          {material.notes && (
                            <p className="text-sm text-gray-600">{material.notes}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Recommendations */}
                  {results.recommendations && (
                    <div className="p-6 bg-blue-50 rounded-2xl border border-blue-100">
                      <h3 className="font-bold text-lg mb-3">Recommendations</h3>
                      <ul className="space-y-2 text-sm text-gray-700">
                        {results.recommendations.map((rec, idx) => (
                          <li key={idx} className="flex items-start">
                            <span className="text-primary mr-2">•</span>
                            <span>{rec}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-3">
                    <Button
                      onClick={() => navigate('/pledge')}
                      className="flex-1 rounded-full bg-primary hover:bg-primary/90"
                      data-testid="pledge-materials-button"
                    >
                      Pledge These Materials
                    </Button>
                    <Button
                      onClick={() => navigate('/products')}
                      variant="outline"
                      className="flex-1 rounded-full border-2"
                      data-testid="browse-products-button"
                    >
                      Browse Products
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AITakeoff;
